// Label rendering + collision avoidance for the MDS perceptual map.
// Port of BrandPerceptialMapping.Mds.ChartView#updateAxisLabels / #updateChartLabels
// (bpm/mds/chart.coffee). Measures live SVG geometry (getBBox/getTotalLength), then runs
// the legacy aytmTextCollisions layout via createTextCollisions().

import * as d3 from 'd3'

import { createTextCollisions } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/textCollisions'
import { removeTagsFromText } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/textUtils'

import { CLS, ID } from '@aytm/app/stats/page/question-views/bpm/components/mds/components/chart/lib/mdsClasses'
import { separateAxisLabels } from '@aytm/app/stats/page/question-views/bpm/components/mds/components/chart/lib/mdsLabelSeparation'

const SENTINEL_TRANSFORM = /translate\(\s*(-?\d+(?:\.\d+)?)[\s,]+(-?\d+(?:\.\d+)?)/

// countLabelWidth: wraps `w{text}w` in a bold span and measures width (or height)
const measureLabel = (text, fontSize = 10, askHeight = false) => {
  if (text == null) return 0
  const span = document.createElement('span')
  span.setAttribute('font-family', 'Ubuntu, Calibri, Segoe, Helvetica, Arial')
  span.style.cssText = `font-weight: bold; fill: black; font-family: Ubuntu, Calibri, Segoe, Helvetica, Arial; font-size: ${fontSize}px; line-height: 12px; position: absolute; top: -100px; right: 0;`
  span.innerHTML = `w${text}w`
  document.body.appendChild(span)
  const rect = span.getBoundingClientRect()
  document.body.removeChild(span)
  return askHeight ? rect.height : rect.width
}

// getTotalLength of the axis path with the given id (string id match — ids contain dots/signs)
const axisPathLength = (svgNode, id) => {
  for (const path of svgNode.querySelectorAll(`path.${CLS.axis}`)) {
    if (path.getAttribute('id') === id) return path.getTotalLength()
  }
  return
}

const stripTags = text => String(text).replace(/(<([^>]+)>)/gi, '')

// Truncate the label html (char-by-char + ellipsis) when it overruns the axis length
const cutText = (html, total) => {
  const tmp = document.createElement('div')
  tmp.innerHTML = html
  const object = tmp.firstChild
  if (!object) return html

  let inner = object.innerHTML
  // A label is never shorter than its own padding, so against an axis with no measurable length
  // the loop below could not end; leave such a label whole.
  if (!(total > 0)) return object.outerHTML
  if (measureLabel(inner) > total / 1.75) {
    while (inner && measureLabel(inner) > total / 2) {
      inner = inner.slice(0, -1)
    }
    inner = `${inner}...`
  }
  object.innerHTML = inner
  return object.outerHTML
}

const computeStartOffset = (svgNode, d) => {
  // the same sides the axis path is drawn by: a horizontal axis belongs to its side like any other
  if (Math.sign(d.x) > 0) {
    const total = axisPathLength(svgNode, ID.line(d.x, d.y))
    const width = measureLabel(String(d.text))
    let offset = (width / total) * 100
    if (offset >= 45) offset = 45
    return `${95 - (offset || 45)}%`
  }
  if (Math.sign(d.x) < 0) {
    return '5%'
  }
  return null
}

const tspan = (axis, contains, text) => {
  const angle = axis.s ? (axis.a * 180) / Math.PI : 180 + (axis.a * 180) / Math.PI
  const weight = axis.index < 3 ? 'bold' : 'normal'
  const fill = contains ? 'black' : '#EEE'
  return `<tspan style='font-weight: ${weight}; fill: ${fill}' data-angle='${angle}' data-id='${axis.id}'>${stripTags(text)}</tspan>`
}

const buildAxisLabelHtml = (svgNode, d, axises, mdsAttributesValue) => {
  const total = axisPathLength(svgNode, ID.line(d.x, d.y))

  let text = ''
  if (Array.isArray(d.text)) {
    d.text.forEach((t, i) => {
      const axis = axises.find(a => a.id === d.id[i])
      const grouped = { ...axis, a: d.a } // grouped label uses the group angle
      text += tspan(grouped, mdsAttributesValue.includes(axis.id), t)
      // legacy separator quirk: compares loop index to accumulated string length → effectively ', '
      text += i === text.length - 1 ? ' ' : ', '
    })
  } else {
    const axis = axises.find(a => a.id === d.id)
    text = tspan(axis, mdsAttributesValue.includes(d.id), d.text)
  }

  return cutText(text, total)
}

/**
 * Append <textPath> labels along each axis into the lines group.
 * @param svgNode root <svg>
 * @param {{ axisesLabels: Array, axises: Array, mdsAttributesValue: string[] }} params
 */
export const updateAxisLabels = (svgNode, { axisesLabels, axises, mdsAttributesValue }) => {
  const linesGroup = d3.select(svgNode).select(`g.${CLS.lines}`)

  const labels = linesGroup
    .selectAll(`text.${CLS.axisLabel}`)
    .data(axisesLabels)
    .enter()
    .append('text')
    .classed(CLS.axisLabel, true)
    .attr('font-family', 'Ubuntu, Calibri, Segoe, Helvetica, Arial')
    .attr('font-size', '10px')
    .attr('line-height', '12px')
    .attr('dy', -6)
    .style('font-family', 'Ubuntu, Calibri, Segoe, Helvetica, Arial')
    .style('font-size', '10px')
    .style('line-height', '12px')

  labels.append('title').html(d => removeTagsFromText(String(d.text)))

  labels
    .append('textPath')
    .attr('href', d => `#${ID.line(d.x, d.y)}`)
    .attr('xlink:href', d => `#${ID.line(d.x, d.y)}`)
    .attr('text-anchor', 'start')
    .attr('startOffset', d => computeStartOffset(svgNode, d))
    .html(d => buildAxisLabelHtml(svgNode, d, axises, mdsAttributesValue))

  // two axes a few degrees apart would lay one label over the other; measured on the drawn text, and
  // done before the point labels are laid out, which read these boxes
  separateAxisLabels(svgNode, { labelClass: CLS.axisLabel, pathClass: CLS.axis })
}

const isSentinelTransform = transform => {
  if (!transform) return false
  const m = SENTINEL_TRANSFORM.exec(transform)
  return Boolean(m) && Number(m[1]) === -100 && Number(m[2]) === -100
}

// Collect bubble/axis/label geometry into the collision layout's points[] shape
const collectCollisionPoints = svgNode => {
  const points = []

  svgNode.querySelectorAll(`.${CLS.points} .${CLS.point}`).forEach((point, i) => {
    const circle = point.querySelector(`circle.${CLS.inner}`)
    const labelText = point.querySelector(`g.${CLS.label} text`)
    const box = labelText.getBBox()
    points.push({
      id: i,
      circle: {
        x: Number(circle.getAttribute('cx')),
        y: Number(circle.getAttribute('cy')),
        r: Number(circle.getAttribute('r'))
      },
      label: { text: labelText.textContent, w: box.width, h: box.height }
    })
  })

  svgNode.querySelectorAll(`.${CLS.axisLines}`).forEach(node => {
    const circle = node.querySelector(`circle.${CLS.endOuter}`)
    points.push({
      circle: {
        x: Number(circle.getAttribute('cx')),
        y: Number(circle.getAttribute('cy')),
        r: Number(circle.getAttribute('r'))
      }
    })
  })

  svgNode.querySelectorAll(`.${CLS.axises} .${CLS.axis}`).forEach(line => {
    const dAttr = line.getAttribute('d')
    if (dAttr == null) return
    const ar = dAttr.split(' ')
    points.push({
      line: { x1: Number(ar[0].slice(1)), y1: Number(ar[1]), x2: Number(ar[2].slice(1)), y2: Number(ar[3]) }
    })
  })

  svgNode.querySelectorAll(`.${CLS.axisLabel}`).forEach(rect => {
    if (!rect.getClientRects().length) return
    const box = rect.getBBox()
    points.push({ rect: { x: Number(box.x), y: Number(box.y), w: Number(box.width), h: Number(box.height) } })
  })

  return points
}

/**
 * Run collision avoidance, position point labels, draw leader lines.
 * @param svgNode root <svg>
 * @param {{ width:number, height:number, selected:string[] }} params
 *   selected = currently selected brand ids (drives leader-line visibility)
 */
export const updateChartLabels = (svgNode, { width, height, selected = [] }) => {
  const collisionPoints = collectCollisionPoints(svgNode)

  const positions = createTextCollisions().size(width, height).setCirclePadding(7).setAngleStep(1).run(collisionPoints)

  // Position the point labels that are still parked at the -100,-100 sentinel
  d3.select(svgNode)
    .selectAll(`.${CLS.points} .${CLS.point} g.${CLS.label}`)
    .each(function (_, i) {
      const item = d3.select(this)
      if (!isSentinelTransform(item.attr('transform'))) return
      const position = positions[i]
      if (!position) return
      const point = collisionPoints.find(p => p.id === i) || { label: { w: 0 } }
      item.attr('transform', `translate(${position.x + point.label.w / 2}, ${position.y})`)
    })

  // Leader lines from each brand point to its label
  d3.select(svgNode)
    .selectAll(`.${CLS.points} .${CLS.point}`)
    .each(function (_, i) {
      if (this.querySelectorAll(`path.${CLS.pointPath}`).length !== 0) return
      const position = positions[i]
      if (!position) return

      const circle = this.querySelector(`circle.${CLS.inner}`)
      const labelText = this.querySelector(`g.${CLS.label} text`)
      const point = collisionPoints.find(p => p.id === i) || { label: { w: 0 } }

      const x1 = Number(circle.getAttribute('cx'))
      const y1 = Number(circle.getAttribute('cy'))
      const x2 = position.x + point.label.w / 2
      let y2 = position.y
      if (y2 > y1) y2 -= measureLabel(labelText.innerHTML, 10, true)

      const id = this.getAttribute('id')
      const isSelected = selected.length === 0 ? false : selected.includes(id)

      d3.select(this)
        .append('path')
        .classed(CLS.pointPath, true)
        .attr('d', `M ${x1} ${y1} L ${x2} ${y2}`)
        .attr('opacity', isSelected ? 1 : 0)
        .attr('stroke', '#CCCCCC')
        .attr('stroke-width', '0.5px')
    })
}
